// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Table, TableHead, TableBody } from '@material-ui/core';
import { TableRow, TableCell } from '@material-ui/core';
import { Card, CardContent } from '@material-ui/core';
import { Flex, Box, useSystem, useResponsive } from 'react-system';
import * as icons from '../../icons';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { useAnalytics } from '../../controls/analytics';
import { supportsHover } from '../../utils/supports-hover';
import { nth } from './utils/nth';
import { MappingImage } from './MappingImage';
import { DevelopmentProductSelectionDialog } from './DevelopmentProductSelectionDialog';
import { DevelopmentProductDetails } from './DevelopmentProductDetails';
import type { DevelopmentLots_development$key } from './__generated__/DevelopmentLots_development.graphql';

type Props = {|
  development: DevelopmentLots_development$key,
|};

const createTableHeaders = t => [
  {
    label: t('ref'),
    props: { align: 'left' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('floor'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('rooms'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('bedRoomsShort'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('grossSurface'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('balconySurfaceShort'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('gardenSurfaceShort'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('terraceSurfaceShort'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('basementSurfaceShort'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },
  {
    label: t('weightedFloorSurface'),
    props: { align: 'right' },
    display: ['none', 'table-cell'],
  },

  {
    label: t('price'),
    props: { align: 'right', colSpan: 2 },
    display: ['none', 'table-cell'],
  },
];

const getFloorKey = (floorOfFlat, t) => {
  if (floorOfFlat === 0) {
    return t('ground');
  }
  return floorOfFlat != null ? nth(floorOfFlat, t) : '-';
};

const getStatusText = (t, status) => {
  switch (status) {
    case 'reserved':
      return t('reserved');
    case 'sold':
      return t('sold');
    default:
      return '';
  }
};

const displayStatus = (product, locale, colors) => {
  const { t, numberLocale, currency } = locale;
  if (['sold', 'reserved'].includes(product.displayStatus)) {
    return (
      <Box
        css={
          {
            sold: {
              color: colors && colors.red[500],
            },
            reserved: {
              color: colors && colors.orange[500],
            },
          }[product.displayStatus === 'sold' ? 'sold' : 'reserved']
        }
      >
        {getStatusText(t, product.displayStatus)}
      </Box>
    );
  }
  if (product.hidePriceOnPortals === false) {
    return product.salePrice != null
      ? numberLocale.format(product.salePrice, {
          style: 'currency',
          currency,
          currencyDisplay: 'symbol',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        })
      : '-';
  }
  return t('onRequest');
};

const getTableCells = (product, locale, theme) => {
  const { t, numberLocale } = locale;
  const formatOptions = {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  };
  const { colors, text } = theme;
  const { property } = product;
  const propertyKeys = Object.keys(property);
  const tableCells = [
    {
      value: (
        <Box my={-2} css={text.subtitle1}>
          {[
            {
              label: t('price'),
              value: displayStatus(product, locale, colors),
            },
            {
              label: t('ref'),
              value: [
                product.lotReference,
                property.floorOfFlat != null ? ' · ' : '',
                getFloorKey(property.floorOfFlat, t),
              ].join(''),
            },

            {
              label: t('rooms'),
              value: property.numberOfRooms,
            },
            {
              label: t('bedRoomsShort'),
              value: property.numberOfBedrooms,
            },
            {
              ...[
                {
                  label: t('grossFloorSurface'),
                  value: property.grossFloorSurface,
                  unit: ' m²',
                },
                {
                  label: t('weightedSurface'),
                  value: property.weightedFloorSurface,
                  unit: ' m²',
                },
                {
                  label: t('usableSurface'),
                  value: property.usableSurface,
                  unit: ' m²',
                },
                {
                  label: t('livingSurface'),
                  value: property.livingSurface,
                  unit: ' m²',
                },
                { label: t('grossFloorSurface'), value: null },
              ]
                .map(item => ({
                  ...item,
                  value: `${
                    item.value != null
                      ? numberLocale.format(item.value, formatOptions)
                      : '-'
                  }`,
                }))
                .filter(({ value }) => value)[0],
            },
          ].map((config, i) => (
            <React.Fragment key={i}>
              <Flex py={[0, 1, 1]} flexWrap="wrap">
                <Box flexGrow={1} css={text.subtitle2}>
                  {config.label}
                </Box>
                <Box
                  flexShrink={0}
                  css={[
                    i === 0 ? text.subtitle2 : text.body2,
                    { textAlign: 'right' },
                  ]}
                >
                  {config.value ?? '-'}
                  {config.unit != null ? config.unit : ''}
                </Box>
              </Flex>
            </React.Fragment>
          ))}
        </Box>
      ),
      props: { align: 'left', colSpan: 8 },
      display: ['table-cell', 'none'],
    },
    {
      value: (
        <Flex justifyContent="center">
          <icons.KeyboardArrowRight css={{ fontSize: '26' }} />
        </Flex>
      ),
      props: { align: 'right', colSpan: 2 },
      display: ['table-cell', 'none'],
    },

    // Normal
    {
      value: product.lotReference ?? '-',
      props: { align: 'left' },
      display: ['none', 'table-cell'],
    },
    {
      value: getFloorKey(property.floorOfFlat, t),
      props: { align: 'right' },
      display: ['none', 'table-cell'],
    },
  ];

  Object.values(property).map((prop, index) => {
    if (['numberOfRooms', 'numberOfBedrooms'].includes(propertyKeys[index])) {
      tableCells.push({
        value: prop != null ? String(prop) : '-',
        props: { align: 'right' },
        display: ['none', 'table-cell'],
      });
    }
  });

  const tableCellsSurface = [
    {
      value: property.grossFloorSurface,
      unit: 'm²',
      props: { align: 'right' },
      display: ['none', 'table-cell'],
    },
    {
      value: property.balconySurface,
      unit: 'm²',
      props: { align: 'right' },
      display: ['none', 'table-cell'],
    },
    {
      value: property.gardenSurface,
      unit: 'm²',
      props: { align: 'right' },
      display: ['none', 'table-cell'],
    },
    {
      value: property.terraceSurface,
      unit: 'm²',
      props: { align: 'right' },
      display: ['none', 'table-cell'],
    },
    {
      value: property.basementSurface,
      unit: 'm²',
      props: { align: 'right' },
      display: ['none', 'table-cell'],
    },
    {
      value: property.weightedFloorSurface,
      unit: 'm²',
      props: { align: 'right' },
      display: ['none', 'table-cell'],
    },
  ].map(item => ({
    ...item,
    value: `${
      item.value != null ? numberLocale.format(item.value, formatOptions) : '-'
    }${item.value != null ? item.unit : ''}`,
  }));

  return [
    ...tableCells,
    ...tableCellsSurface,
    {
      value: displayStatus(product, locale, colors),
      props: { align: 'right', colSpan: 2 },
      display: ['none', 'table-cell'],
    },
  ];
};

export const DevelopmentLots = (props: Props): React.Node => {
  const theme = useTheme();
  const { text } = theme;
  const locale = useLocale();
  const { t } = locale;
  const responsive = useResponsive();
  const { media } = useSystem();
  const { track } = useAnalytics();
  const [lot, setLot] = React.useState(null);
  const [hoverIndex, setHoverIndex] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const development = useFragment(
    graphql`
      fragment DevelopmentLots_development on DevelopmentProduct {
        ...DevelopmentProductSelectionDialog_development
        id
        mappingImage {
          ...MappingImage_image
        }
        products {
          ...DevelopmentProductDetails_product
          ...MappingImage_products
          id
          lotReference
          salePrice
          displayStatus
          hidePriceOnPortals
          property {
            floorOfFlat
            numberOfRooms
            numberOfBedrooms
            grossFloorSurface
            balconySurface
            gardenSurface
            terraceSurface
            basementSurface
            weightedFloorSurface
            livingSurface
            usableSurface
          }
        }
      }
    `,
    props.development,
  );

  const { products } = development;
  const tableHeaders = createTableHeaders(t);
  return (
    <>
      <Box py={3} mb={3} css={media([text.h6, text.h5])}>
        {t('lots')}
      </Box>
      <Box
        mx={['-16px', 0]}
        css={media({
          borderRadius: [0, '4'],
          border: '#e0e0e0 1px solid',
        })}
      >
        {development.mappingImage != null && (
          <Box>
            <MappingImage
              products={products}
              hoverIndex={hoverIndex}
              image={development.mappingImage}
              onProductHover={setHoverIndex}
              onProductClick={index => {
                track('RA.Web.Development.Mapping.Click', {
                  developmentId: development.id,
                  lotId: products[index].id,
                });

                setLot(products[index]);
                setDialogOpen(true);
              }}
              hoverCard={hoverIndex => (
                <Card elevation={6}>
                  <CardContent>
                    <Box px={2}>
                      <DevelopmentProductDetails
                        product={products[hoverIndex]}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            />
          </Box>
        )}
        <Table>
          <TableHead css={media({ display: ['none', 'table-header-group'] })}>
            <TableRow>
              {tableHeaders.map((th, i) => {
                return (
                  <TableCell
                    css={[text.subtitle2, media({ display: th.display })]}
                    key={'header-cell-' + i}
                    {...th.props}
                  >
                    {th.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product, index) => {
              // If we set these handlers on iOS,
              // the first click will triger a hover instead of a click:
              // users will have to click twice.
              const onMouseEnter = () => {
                if (supportsHover === true) {
                  setHoverIndex(index);
                }
              };
              const onMouseLeave = () => {
                if (supportsHover === true && hoverIndex === index) {
                  setHoverIndex(null);
                }
              };

              return (
                <TableRow
                  key={product.id}
                  selected={product.displayStatus === 'sold'}
                  hover={true}
                  css={{ cursor: 'pointer' }}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  onClick={() => {
                    track('RA.Web.Development.Lots.Click', {
                      developmentId: development.id,
                      lotId: product.id,
                    });

                    setLot(product);
                    setDialogOpen(true);
                  }}
                >
                  {getTableCells(product, locale, theme).map((cell, i) => (
                    <TableCell
                      key={'cell-' + i}
                      {...cell.props}
                      css={[
                        media({ display: cell.display }),
                        index + 1 === products.length
                          ? { borderBottom: 'none' }
                          : {},
                      ]}
                    >
                      {cell.value}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {lot != null && (
        <DevelopmentProductSelectionDialog
          animation={responsive(['left', 'up'])}
          dialogOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          development={development}
          productId={lot.id}
        />
      )}
    </>
  );
};
